import { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Location from 'expo-location';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Body, Caption } from '@/components/ui/Text';
import type { ItineraryItem } from '@/services/itinerary';
import type { Trip } from '@/services/trips';
import {
  getMobilitySummaryLine,
  shouldShowGettingAround,
} from '@/services/mobilityRecommendations';
import { getDestinationCoordinates } from '@/services/weather';

type GettingAroundRowProps = {
  trip: Trip;
  item: ItineraryItem;
  isTripActive: boolean;
  onOpenMobility: (itemId: string) => void;
};

export function GettingAroundRow({
  trip,
  item,
  isTripActive,
  onOpenMobility,
}: GettingAroundRowProps) {
  const theme = useTheme();
  const [summary, setSummary] = useState<string | null>(null);

  const visible = shouldShowGettingAround(trip, item, isTripActive);
  const destinationLabel = (item.address || item.location || '').trim();

  useEffect(() => {
    if (!visible || !destinationLabel) {
      setSummary(null);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const dest = await getDestinationCoordinates(destinationLabel);
        if (!dest || cancelled) return;

        let origin = dest;
        if (isTripActive) {
          const permission = await Location.requestForegroundPermissionsAsync();
          if (permission.status === 'granted') {
            const current = await Location.getCurrentPositionAsync({
              accuracy: Location.Accuracy.Balanced,
            });
            origin = {
              latitude: current.coords.latitude,
              longitude: current.coords.longitude,
            };
          }
        }

        const line = await getMobilitySummaryLine(origin, dest, trip.budget?.currency || 'USD');
        if (!cancelled) {
          setSummary(line);
        }
      } catch {
        if (!cancelled) {
          setSummary(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [destinationLabel, isTripActive, trip.budget?.currency, visible]);

  if (!visible) {
    return null;
  }

  const subtitle = summary || 'Compare walk, transit, and ride options';

  return (
    <Pressable
      onPress={() => onOpenMobility(item.id)}
      accessibilityRole="button"
      accessibilityLabel="Getting around"
      accessibilityHint="Opens mobility options for this activity"
      style={({ pressed }) => ({ opacity: pressed ? 0.85 : 1 })}
    >
      <Card variant="outlined" style={{ padding: theme.spacing(1.25) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
          <Ionicons name="navigate-outline" size={20} color={theme.colors.primary} />
          <View style={{ flex: 1, minWidth: 0, gap: theme.spacing(0.25) }}>
            <Body style={{ fontWeight: '700' }}>Getting around</Body>
            <Caption color={theme.colors.textSecondary} numberOfLines={2}>
              {subtitle}
            </Caption>
          </View>
          <Ionicons name="chevron-forward" size={18} color={theme.colors.textSecondary} />
        </View>
      </Card>
    </Pressable>
  );
}
